import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Drama, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const AuthModalContext = createContext({ openAuthModal: () => {} });
export const useAuthModal = () => useContext(AuthModalContext);

function AuthModal({ message, onClose, onSuccess }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, firstName.trim(), lastName.trim());
      toast.success(mode === "login" ? "Welcome back" : "Your watchlist is ready");
      await onSuccess();
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "mt-1 w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring";

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-sm rounded-2xl border border-amber-900/30 bg-card p-7 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2">
          <Drama className="h-5 w-5 text-[hsl(var(--gold))]" />
          <span className="font-serif text-lg font-semibold">My Theatre</span>
        </div>
        <h2 className="mt-4 font-serif text-2xl font-semibold">
          {mode === "login" ? "Sign in to continue" : "Create your account"}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {message || "Follow shows and performers and get alerted the moment something changes."}
        </p>

        <form onSubmit={submit} className="mt-5 space-y-3">
          {mode === "register" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">First name</label>
                <input
                  required
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Last name</label>
                <input
                  required
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          )}
          <div>
            <label className="text-sm font-medium">Email</label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
              placeholder="••••••••"
            />
          </div>
          {mode === "login" && (
            <div className="text-right">
              <a href="/forgot-password" className="text-sm text-primary hover:underline">
                Forgot password?
              </a>
            </div>
          )}
          {err && <p className="text-sm text-destructive">{err}</p>}
          <button
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground transition-colors hover:bg-[#B82332] disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <p className="mt-5 text-center text-sm text-muted-foreground">
          {mode === "login" ? "No account yet?" : "Already have an account?"}{" "}
          <button
            type="button"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
            className="font-semibold text-primary hover:underline"
          >
            {mode === "login" ? "Sign up" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
}

export function AuthModalProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const pending = useRef(null);

  const openAuthModal = useCallback((onSuccess, msg) => {
    pending.current = typeof onSuccess === "function" ? onSuccess : null;
    setMessage(msg || "");
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    pending.current = null;
    setOpen(false);
  }, []);

  const handleSuccess = useCallback(async () => {
    const run = pending.current;
    pending.current = null;
    setOpen(false);
    if (run) {
      try { await run(); } catch (e) { console.error(e); }
    }
  }, []);

  return (
    <AuthModalContext.Provider value={{ openAuthModal }}>
      {children}
      {open && <AuthModal message={message} onClose={close} onSuccess={handleSuccess} />}
    </AuthModalContext.Provider>
  );
}